import React from 'react';

const RatingVisualization = ({ rating, title = "Admission Likelihood" }) => {
  console.log('📈 RatingVisualization received:', { rating, ratingType: typeof rating, title });
  
  // Map text ratings to numeric values for visualization
  const getRatingValue = (rating) => {
    console.log('🔢 getRatingValue called with:', { rating, ratingType: typeof rating });
    
    if (!rating) {
      console.log('❌ No rating provided');
      return 0;
    }
    
    // If it's already a number, use it directly
    if (typeof rating === 'number') {
      console.log('✅ Rating is already a number:', rating);
      return rating;
    }
    
    // Ensure rating is a string before processing
    const ratingStr = typeof rating === 'string' ? rating : String(rating);
    
    // Look for "Consensus Rating: X.X" or "Rating: X.X" patterns first
    const consensusRatingMatch = ratingStr.match(/consensus\s+rating:\s*(\d+(?:\.\d+)?)/i);
    if (consensusRatingMatch) {
      const numericValue = parseFloat(consensusRatingMatch[1]);
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 5) {
        console.log('✅ Parsed consensus rating:', numericValue);
        return numericValue;
      }
    }
    
    // Look for general "Rating: X.X" patterns
    const generalRatingMatch = ratingStr.match(/(?:overall\s+)?rating:\s*(\d+(?:\.\d+)?)/i);
    if (generalRatingMatch) {
      const numericValue = parseFloat(generalRatingMatch[1]);
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 5) {
        console.log('✅ Parsed general rating:', numericValue);
        return numericValue;
      }
    }
    
    // Check if it's a numeric string (like "2.5", "3", "4.0")
    const numericMatch = ratingStr.match(/^(\d+(?:\.\d+)?)(?:\/5)?$/);
    if (numericMatch) {
      const numericValue = parseFloat(numericMatch[1]);
      if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 5) {
        console.log('✅ Parsed numeric rating:', numericValue);
        return numericValue;
      }
    }
    
    const normalizedRating = ratingStr.toLowerCase().trim();
    console.log('🔤 Processing string rating:', { ratingStr, normalizedRating });
    
    // First, look for the specific format we ask AI to use
    const specificRatingMatch = normalizedRating.match(/\*\*overall\s+rating:\s*\[(.*?)\]\*\*/i);
    if (specificRatingMatch) {
      const ratingText = specificRatingMatch[1].toLowerCase().trim();
      
      if (ratingText === 'high') return 5;
      if (ratingText === 'medium-high') return 4;
      if (ratingText === 'medium') return 3;
      if (ratingText === 'medium-low') return 2;
      if (ratingText === 'low') return 1;
    }
    
    // Look for other common rating formats
    const ratingFormats = [
      /overall\s+rating:\s*(high|medium-high|medium|medium-low|low)/i,
      /rating:\s*(high|medium-high|medium|medium-low|low)/i,
      /assessment:\s*(high|medium-high|medium|medium-low|low)/i,
      /likelihood:\s*(high|medium-high|medium|medium-low|low)/i
    ];
    
    for (const format of ratingFormats) {
      const match = normalizedRating.match(format);
      if (match) {
        const ratingText = match[1].toLowerCase().trim();
        
        if (ratingText === 'high') return 5;
        if (ratingText === 'medium-high') return 4;
        if (ratingText === 'medium') return 3;
        if (ratingText === 'medium-low') return 2;
        if (ratingText === 'low') return 1;
      }
    }
    
    // Fall back to general pattern matching
    const ratingPatterns = [
      { pattern: /\b(high|strong|excellent|very\s+good|5\/5|outstanding)\b/i, value: 5 },
      { pattern: /\b(medium-high|medium\s+high|good|competitive|4\/5|above\s+average)\b/i, value: 4 },
      { pattern: /\b(medium|moderate|average|fair|3\/5|viable)\b/i, value: 3 },
      { pattern: /\b(medium-low|medium\s+low|below\s+average|challenging|2\/5|difficult)\b/i, value: 2 },
      { pattern: /\b(low|poor|weak|unlikely|1\/5|very\s+difficult)\b/i, value: 1 }
    ];
    
    // Check for patterns in order of preference (highest to lowest)
    for (const { pattern, value } of ratingPatterns) {
      if (pattern.test(normalizedRating)) {
        return value;
      }
    }
    
    console.log('🔄 No pattern matched, returning 0');
    return 0; // Default
  };

  const getRatingLabel = (value) => {
    if (value >= 4.5) return 'High';
    if (value >= 3.5) return 'Medium-High';
    if (value >= 2.5) return 'Medium';
    if (value >= 1.5) return 'Medium-Low';
    if (value >= 0.5) return 'Low';
    return 'Not Evaluated';
  };

  const getRatingColor = (value) => {
    if (value >= 4.5) return '#10B981'; // Green
    if (value >= 3.5) return '#59CC51'; // Light Green
    if (value >= 2.5) return '#F59E0B'; // Yellow
    if (value >= 1.5) return '#F97316'; // Orange
    if (value >= 0.5) return '#EF4444'; // Red
    return '#6B7280'; // Gray
  };

  const getRatingDescription = (value) => {
    if (value >= 4.5) return 'Strong candidate with excellent chances';
    if (value >= 3.5) return 'Competitive candidate with good chances';
    if (value >= 2.5) return 'Viable candidate with moderate chances';
    if (value >= 1.5) return 'Challenging profile, needs strengthening';
    if (value >= 0.5) return 'Difficult admission, major improvements needed';
    return 'Evaluation pending';
  };

  const ratingValue = getRatingValue(rating);
  const ratingLabel = getRatingLabel(ratingValue);
  const ratingColor = getRatingColor(ratingValue);
  const ratingDescription = getRatingDescription(ratingValue);
  
  console.log('🎯 Final rating values:', { 
    ratingValue, 
    ratingLabel, 
    ratingColor, 
    ratingDescription 
  });

  return (
    <div className="rating-visualization">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
        
        {/* Rating Scale */}
        <div className="relative mb-4">
          {/* Background gradient bar */}
          <div className="h-6 rounded-full bg-gradient-to-r from-red-400 via-orange-400 via-yellow-400 via-green-400 to-green-500 relative">
            {/* Indicator */}
            {ratingValue > 0 && (
              <div
                className="absolute top-0 h-6 w-6 bg-white border-2 rounded-full shadow-lg transform -translate-x-1/2 flex items-center justify-center"
                style={{
                  left: `${(ratingValue / 5) * 100}%`,
                  borderColor: ratingColor,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: ratingColor }}
                />
              </div>
            )}
          </div>
          
          {/* Scale labels */}
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Low</span>
            <span>Medium-Low</span>
            <span>Medium</span>
            <span>Medium-High</span>
            <span>High</span>
          </div>
        </div>

        {/* Rating Details */}
        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: ratingColor }}
            />
            <div>
              <div className="font-semibold text-gray-900">{ratingLabel}</div>
              <div className="text-sm text-gray-600">{ratingDescription}</div>
            </div>
          </div>
          
          {/* Percentage indicator */}
          <div className="text-right">
            <div className="font-semibold text-gray-900">
              {ratingValue > 0 ? `${ratingValue}/5` : 'N/A'}
            </div>
            <div className="text-xs text-gray-500">
              {ratingValue > 0 ? `${(ratingValue / 5 * 100).toFixed(0)}%` : 'Pending'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingVisualization; 